/* ---------------------------------------------------------------------------
   De dónde se entró a una pantalla compartida.

   Hay pantallas a las que se llega desde varios lugares y cuya URL es la MISMA
   en todos los casos, así que el origen no se puede deducir de ella:

   - el DOCUMENTO de cotización, desde la edición del informe, la confirmación
     del envío a revisión y el visado del revisor;
   - la EDICIÓN del informe, desde la cola del ejecutivo, la bandeja del
     revisor, el visado y la confirmación del envío.

   Sin declarar el origen, el "Volver" es fijo y devuelve al usuario a una
   pantalla por la que nunca pasó — y con él se equivocan también el camino de
   miga del `TopBar` y la fila resaltada del menú, que afirman el mismo
   recorrido inventado.

   POR QUÉ EN LA QUERY STRING y no en `location.state`: el estado del router se
   pierde al recargar, al compartir el enlace y al volver con el historial —es
   exactamente el motivo por el que `CotizacionEnviada` dejó de ser un estado
   local del formulario y pasó a ser una ruta. Un "Volver" que se rompe con F5
   es peor que uno fijo, porque falla cuando nadie lo está mirando.

   POR QUÉ UN ORIGEN NOMBRADO y no la url de retorno cruda (`?volver=/informes/7`):
   una url que viene de la barra de direcciones es un destino que el usuario
   controla. Con un vocabulario CERRADO, un valor inventado no puede apuntar a
   ninguna parte — cae al retorno por defecto de cada pantalla.

   POR QUÉ UN SOLO VOCABULARIO para las dos pantallas: los orígenes son las
   MISMAS pantallas del sistema (la edición del informe es origen del documento
   y destino de la bandeja a la vez). Con una lista por destino, "informe"
   estaría declarado dos veces y su rótulo podría divergir.
--------------------------------------------------------------------------- */

/** Las pantallas del sistema que pueden ser el origen de una navegación. */
export type Origen = "informes" | "revisiones" | "informe" | "cotizacion" | "visado";

/** Nombre del parámetro en la URL. Uno solo, para no escribirlo a mano. */
export const PARAM_ORIGEN = "desde";

/**
 * A dónde vuelve el DOCUMENTO cuando nadie declaró origen: enlace viejo, URL
 * pegada a mano o marcador.
 *
 * Es `informe` porque la ficha del informe SIEMPRE existe y siempre es válida
 * —es el ancestro del documento en la propia URL—, mientras que las otras dos
 * dependen del estado: "Cotización enviada a revisión" miente en cuanto la
 * cotización se aprueba, y el visado es de un perfil que puede no ser el que
 * está mirando.
 */
export const ORIGEN_DEL_DOCUMENTO: Origen = "informe";

/**
 * Lo mismo para la EDICIÓN del informe: `/informes` es su ancestro en la URL,
 * existe siempre, y todo informe editable figura en esa cola. Es además el
 * destino que la pantalla tenía fijo, así que una url sin origen se comporta
 * como antes.
 */
export const ORIGEN_DE_LA_EDICION: Origen = "informes";

const ORIGENES: readonly Origen[] = [
  "informes",
  "revisiones",
  "informe",
  "cotizacion",
  "visado",
];

function paramsDe(search: string | URLSearchParams): URLSearchParams {
  return typeof search === "string" ? new URLSearchParams(search) : search;
}

/**
 * El origen declarado, o `null` si no viene o es inventado.
 *
 * Devuelve `null` en vez de un valor por defecto porque hay dos preguntas
 * distintas: "¿a dónde vuelvo?" —que siempre necesita una respuesta, ver
 * {@see leerOrigen}— y "¿el usuario declaró de dónde viene?", que es la que
 * usa el menú para decidir si el prefijo de la ruta manda o no.
 */
export function origenDeclarado(search: string | URLSearchParams): Origen | null {
  const declarado = paramsDe(search).get(PARAM_ORIGEN);

  return ORIGENES.find((origen) => origen === declarado) ?? null;
}

/**
 * El origen declarado, o el que la pantalla declare por defecto.
 *
 * Valida contra la lista en vez de castear: el valor viene de la URL, así que
 * `?desde=cualquier-cosa` es un caso normal, no un error de programación.
 */
export function leerOrigen(search: string | URLSearchParams, defecto: Origen): Origen {
  return origenDeclarado(search) ?? defecto;
}

/** A dónde vuelve cada origen, y con qué rótulo. */
export function retornoA(origen: Origen, informeId: number): { to: string; etiqueta: string } {
  switch (origen) {
    case "informes":
      return { to: "/informes", etiqueta: "Volver a informes en proceso" };
    case "revisiones":
      return { to: "/revisiones", etiqueta: "Volver a la bandeja" };
    case "cotizacion":
      return {
        to: `/informes/${informeId}/cotizacion/enviada`,
        etiqueta: "Volver a la cotización",
      };
    case "visado":
      return { to: `/revisiones/${informeId}`, etiqueta: "Volver al visado" };
    case "informe":
      return { to: `/informes/${informeId}`, etiqueta: "Volver al informe" };
  }
}

/**
 * La RAÍZ de la sección a la que pertenece el origen: la cola del ejecutivo o
 * la bandeja del revisor.
 *
 * Sirve para dos cosas distintas y por el mismo motivo —son las dos únicas
 * pantallas que existen siempre, sin depender de un informe concreto—:
 *
 * 1. Qué fila del menú se resalta ({@see seccionDelOrigen}).
 * 2. A dónde salir cuando el informe NO cargó. Ahí el retorno normal puede ser
 *    inalcanzable —`visado` y `cotizacion` cuelgan del mismo informe roto— así
 *    que se sube un nivel en vez de mandar a otro error.
 */
export function raizDelOrigen(origen: Origen): "informes" | "revisiones" {
  switch (origen) {
    case "revisiones":
    case "visado":
      return "revisiones";
    case "informes":
    case "informe":
    case "cotizacion":
      return "informes";
  }
}

/**
 * La fila del menú que le corresponde a cada origen.
 *
 * El menú resalta la SECCIÓN en la que se está trabajando, y con pantallas
 * compartidas la ruta ya no alcanza para saberlo: `/informes/7` es trabajo de
 * la cola del ejecutivo o de la bandeja del revisor según de dónde se entró.
 *
 * Las dos raíces se llaman igual que su ruta, que es lo que permite derivarla
 * en vez de mantener una segunda tabla que podría divergir.
 */
export function seccionDelOrigen(origen: Origen): "/informes" | "/revisiones" {
  return `/${raizDelOrigen(origen)}`;
}

/** La sección declarada en una url, o `null` si no declara origen. */
export function seccionDeclarada(search: string | URLSearchParams): string | null {
  const origen = origenDeclarado(search);

  return origen ? seccionDelOrigen(origen) : null;
}

/**
 * Las urls de las pantallas compartidas, con su origen ya declarado.
 *
 * Existen para que ningún llamador pueda olvidarse del parámetro ni escribir
 * mal la ruta: varias copias de la url escritas a mano se desincronizan y
 * nadie se entera hasta el 404. Mismo criterio que `ACCESO_RAPIDO` en el menú.
 */
export function urlDeLaEdicion(informeId: number, origen: Origen): string {
  return `/informes/${informeId}?${PARAM_ORIGEN}=${origen}`;
}
